import { memo } from 'react';
import { Claim } from '@scale/shared/types/queue';

import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import ClaimModal from './ClaimModal';
import { PayRateType } from '@scale/shared/defaults';

type PaidTimeExceededModalProps = {
  overtimeRate?: string;
  onStayOnTask: () => void;
  claim?: Claim;
  payRateType?: PayRateType;
  projectId?: string;
  assignment?: AssignmentJson;
  taskId?: string;
};

const exceededTimeModalText = (
  deadlineStr: string,
  payRateType?: PayRateType,
  overtimeRate?: string,
) => {
  if (payRateType === PayRateType.Hourly) {
    return (
      <div className="text-sm m-5">
        Your active task time has ended, but you may continue working at the rate of{' '}
        <b>{overtimeRate}/hr</b> until the submission deadline on <b>{deadlineStr}</b>.
      </div>
    );
  }

  // payRateType === PayRateType.HourlyTraining
  return (
    <div className="flex flex-col gap-2 mr-2 ml-2">
      <div>
        You have exceeded the maximum time for which you can work on this task. You may continue
        working on the task at the same rate in order to submit it.
      </div>
      <div className="text-xs mt-2">
        To receive payment, submit before the submission deadline on <b>{deadlineStr}</b>.
        Submissions will not be accepted after this time.
      </div>
    </div>
  );
};

function PaidTimeExceededModal({
  claim,
  payRateType,
  overtimeRate,
  onStayOnTask,
  projectId,
  assignment,
  taskId,
}: PaidTimeExceededModalProps) {
  const { maxClaim } = useClaimTime(claim, projectId, assignment, taskId);

  if (!claim) {
    return null;
  }

  // only show modal for hourly pay
  if (![PayRateType.Hourly, PayRateType.HourlyTraining].includes(payRateType as PayRateType)) {
    return null;
  }

  const deadlineStr = new Intl.DateTimeFormat('default', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(maxClaim);

  return (
    <ClaimModal
      onConfirmButtonClick={onStayOnTask}
      confirmButtonText="Continue task"
      title="Task time exceeded"
      subtitle={exceededTimeModalText(deadlineStr, payRateType, overtimeRate)}
    />
  );
}

export default memo(PaidTimeExceededModal);
